import { Timestamp } from 'firebase/firestore';

/**
 * Allocation type enumeration.
 */
export const AllocationTypes = {
  BodyShop: 'BodyShop',
  OpenScope: 'OpenScope',
  ClosedScope: 'ClosedScope',
  Absence: 'Absence',
  Operational: 'Operational',
  Commercial: 'Commercial',
  Support: 'Support',
  Product: 'Product',
  Provisional: 'Provisional',
} as const;

export type AllocationType = (typeof AllocationTypes)[keyof typeof AllocationTypes];

export const ALLOCATION_TYPE_METADATA: Record<
  AllocationType,
  { label: string; description: string }
> = {
  BodyShop: {
    label: 'Body Shop',
    description: 'Alocação em regime de body shop',
  },
  OpenScope: {
    label: 'Escopo Aberto',
    description: 'Alocação é em um projeto de escopo aberto',
  },
  ClosedScope: {
    label: 'Escopo Fechado',
    description: 'Alocação em um projeto de escopo fechado',
  },
  Absence: {
    label: 'Ausência',
    description: 'Período de ausência do colaborador',
  },
  Operational: {
    label: 'Interno',
    description: 'Alocação em atividade administrativa, suporte ou produto da Raro.',
  },
  Commercial: {
    label: 'Comercial',
    description: 'Alocação em algum esforço comercial',
  },
  Support: {
    label: 'Suporte',
    description: 'Alocação em contrato de suporte',
  },
  Product: {
    label: 'Aviso Prévio',
    description: 'Colaboradores programados para deixar a organização.',
  },
  Provisional: {
    label: 'Provisória',
    description: 'Alocação temporária enquanto aguarda uma alocação definitiva',
  },
};

export const AllocationDurationTypes = {
  Undefined: 'Undefined',
  Defined: 'Defined',
} as const;

export type AllocationDurationType =
  (typeof AllocationDurationTypes)[keyof typeof AllocationDurationTypes];

export const ALLOCATION_DURATION_METADATA: Record<
  AllocationDurationType,
  { label: string; description: string }
> = {
  Undefined: {
    label: 'Indefinida',
    description: 'A duração não tem duração previamente definida',
  },
  Defined: {
    label: 'Definida',
    description: 'A data final da alocação é conhecida',
  },
};

export const AllocationBucketTypes = {
  Defined: 'Defined',
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
} as const;

export type AllocationBucketType =
  (typeof AllocationBucketTypes)[keyof typeof AllocationBucketTypes];

export const ALLOCATION_BUCKET_METADATA: Record<
  AllocationBucketType,
  { label: string; description: string }
> = {
  Defined: {
    label: 'Pré-definidas',
    description: 'A quantidade de horas da alocação já está estabelecida',
  },
  Daily: {
    label: 'Diário',
    description: 'Número de horas previstas para cada dia de alocação',
  },
  Weekly: {
    label: 'Semanal',
    description: 'Número de horas previstas para cada semana de alocação',
  },
  Monthly: {
    label: 'Mensal',
    description: 'Número de horas previstas para cada mês de alocação',
  },
};

export const AllocationStatuses = {
  Active: 'Active',
  Cancelled: 'Cancelled',
  Finished: 'Finished',
  Scheduled: 'Scheduled',
} as const;

export type AllocationStatus = (typeof AllocationStatuses)[keyof typeof AllocationStatuses];

export interface CollaboratorAllocation {
  id: string;
  allocationType: AllocationType;
  durationType: AllocationDurationType;
  bucketType: AllocationBucketType;
  bucket: number;
  startDate: Timestamp;
  endDate?: Timestamp | null;
  status: AllocationStatus;
  clientId?: string | null;
  clientName?: string | null;
  clientLogo?: string | null;
  clientResponsible?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  capacityId?: string | null;
  capacityLabel?: string | null;
  capacityTypeId?: string | null;
  capacityTypeName?: string | null;
  capacityTechnologyId?: string | null;
  capacityTechnologyName?: string | null;
  capacitySeniorityId?: string | null;
  capacitySeniorityName?: string | null;
  rate?: number | null;
  responsibleId?: string | null;
  responsibleName?: string | null;
  responsibleAvatar?: string | null;
  createdById: string;
  createdByName: string;
  createdByAvatar?: string | null;
  updatedById?: string | null;
  updatedByName?: string | null;
  updatedByAvatar?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  cancelReason?: string | null;
  canceledAt?: Timestamp | null;
  canceledById?: string | null;
  canceledByName?: string | null;
  canceledByAvatar?: string | null;
  noticeReason?: string | null;
  terminationAppliedAt?: Timestamp | null;
}

export interface AllocationCreate
  extends Omit<
    CollaboratorAllocation,
    'id' | 'createdAt' | 'updatedAt'
  > {}

export interface AllocationUpdate
  extends Partial<
    Omit<CollaboratorAllocation, 'id' | 'createdAt' | 'updatedAt'>
  > {
  id: string;
}
